import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, Platform,
} from 'react-native';
import { useLocalSearchParams, useRouter, useNavigation } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../../src/context/OwnerScopeContext';
import { useTheme } from '../../../../src/context/ThemeContext';
import { useNetwork } from '../../../../src/context/NetworkContext';
import { useToast } from '../../../../src/components/Toast';
import ContentContainer from '../../../../src/components/ContentContainer';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../../src/utils/theme';
import type { ColorTheme } from '../../../../src/utils/theme';
import PageFooter from '../../../../src/components/PageFooter';

interface OrderItem { id: string; productName: string; quantity: number; priceInCents: number }
interface Order {
  id: string; status: string; totalInCents: number; shippingCostInCents: number;
  shippingName: string; shippingLine1: string; shippingLine2: string | null;
  shippingCity: string; shippingState: string; shippingZip: string;
  createdAt: string; updatedAt: string; items: OrderItem[];
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#eab308', processing: '#3b82f6', shipped: '#8b5cf6',
  delivered: '#059669', cancelled: '#dc2626',
};

export default function OrderDetail({ entityId }: { entityId?: string } = {}) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { isOffline } = useNetwork();
  const router = useRouter();
  const navigation = useNavigation();
  const scope = useOwnerScope();
  const params = useLocalSearchParams<{ id: string; fromCheckout?: string }>();
  const { fromCheckout } = params;
  const id = entityId ?? params.id;
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fromCheckout) return;
    navigation.setOptions({
      headerLeft: () => (
        <TouchableOpacity onPress={() => router.replace('/owner/controllers' as never)} style={{ paddingRight: 8, paddingLeft: Platform.OS === 'ios' ? 0 : 8 }}>
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
      ),
    });
  }, [fromCheckout, colors]);

  useEffect(() => {
    if (!id) return;
    scope.api.api<Order>(`/api/hardware/orders/${id}`)
      .then(setOrder)
      .catch((err: any) => {
        if (!isOffline) {
          toast.show({ type: 'error', title: 'Couldn’t load order', message: err?.message ?? 'Try again in a moment.' });
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <ContentContainer role="owner">
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      </ContentContainer>
    );
  }

  if (!order) {
    return (
      <ContentContainer role="owner">
        <View style={styles.emptyState}>
          <Text style={styles.emptyTitle}>Order not found</Text>
        </View>
      </ContentContainer>
    );
  }

  const statusColor = STATUS_COLORS[order.status] || colors.textSecondary;

  return (
    <ContentContainer role="owner" footer={false}>
      <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 40 }}>
        <View style={styles.statusRow}>
          <View style={[styles.badge, { backgroundColor: statusColor + '20' }]}>
            <Text style={[styles.badgeText, { color: statusColor }]}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </Text>
          </View>
          <Text style={styles.date}>
            {new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString()}
          </Text>
        </View>

        <Text style={styles.sectionTitle}>Items</Text>
        <View style={styles.card}>
          {order.items.map(item => (
            <View key={item.id} style={styles.itemRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.itemName}>{item.productName}</Text>
                <Text style={styles.itemQty}>Qty: {item.quantity} @ ${(item.priceInCents / 100).toFixed(2)} each</Text>
              </View>
              <Text style={styles.itemTotal}>${((item.priceInCents * item.quantity) / 100).toFixed(2)}</Text>
            </View>
          ))}
          <View style={[styles.itemRow, styles.subtotalRow]}>
            <Text style={styles.subtotalLabel}>Subtotal</Text>
            <Text style={styles.subtotalValue}>${((order.totalInCents - order.shippingCostInCents) / 100).toFixed(2)}</Text>
          </View>
          <View style={styles.itemRow}>
            <Text style={styles.subtotalLabel}>Shipping</Text>
            <Text style={styles.subtotalValue}>${(order.shippingCostInCents / 100).toFixed(2)}</Text>
          </View>
          <View style={[styles.itemRow, styles.totalRow]}>
            <Text style={styles.totalLabel}>Total</Text>
            <Text style={styles.totalPrice}>${(order.totalInCents / 100).toFixed(2)}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Shipping Address</Text>
        <View style={styles.card}>
          <Text style={styles.addressText}>{order.shippingName}</Text>
          <Text style={styles.addressText}>{order.shippingLine1}</Text>
          {order.shippingLine2 && <Text style={styles.addressText}>{order.shippingLine2}</Text>}
          <Text style={styles.addressText}>{order.shippingCity}, {order.shippingState} {order.shippingZip}</Text>
        </View>

        <Text style={styles.orderId}>Order ID: {order.id}</Text>
        <PageFooter />
      </ScrollView>
    </ContentContainer>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, padding: spacing.md },
  statusRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  badge: { paddingHorizontal: 14, paddingVertical: 5, borderRadius: 14 },
  badgeText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold },
  date: { fontSize: fontSize.sm, color: c.textSecondary },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm, marginTop: spacing.md },
  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    borderWidth: 1, borderColor: c.borderLight,
  },
  itemRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8 },
  itemName: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  itemQty: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  itemTotal: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  subtotalRow: { borderTopWidth: 1, borderTopColor: c.borderLight, marginTop: spacing.xs, paddingTop: spacing.sm },
  subtotalLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  subtotalValue: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.medium },
  totalRow: { borderTopWidth: 1, borderTopColor: c.borderLight, marginTop: spacing.xs, paddingTop: spacing.sm },
  totalLabel: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  totalPrice: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.primary },
  addressText: { fontSize: fontSize.sm, color: c.text, lineHeight: 22 },
  orderId: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.lg, textAlign: 'center' },
  emptyState: { alignItems: 'center', paddingTop: 80 },
  emptyTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
